import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Reusable confirmation dialog for the Remediation Hub actions.
 *
 * Modes:
 *   - "match" — user must type `requiredText` exactly (Run-now).
 *   - "checkbox" — user checks acknowledgement next to `requiredText` (Approve).
 *   - "reason" — optional 500-char textarea (Cancel reason).
 *   - "simple" — yes/no with no extra input.
 *
 * Returns an onConfirm callback that gets { value, reason }.
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmColor = "primary",
  mode = "simple",
  requiredText,
  preview, // optional React node rendered above the textbox
  reasonLabel = "Reason (optional)",
  isSubmitting = false,
}) => {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [ackChecked, setAckChecked] = useState(false);

  useEffect(() => {
    if (!open) {
      setTyped("");
      setReason("");
      setAckChecked(false);
    }
  }, [open]);

  const canConfirm = (() => {
    if (isSubmitting) return false;
    if (mode === "match") return typed === requiredText;
    if (mode === "checkbox") return ackChecked;
    return true;
  })();

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({ value: typed, reason: reason || null });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {description && (
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {description}
          </Typography>
        )}
        {preview && <Box sx={{ mb: 2 }}>{preview}</Box>}
        {mode === "checkbox" && requiredText && (
          <FormControlLabel
            sx={{
              alignItems: "flex-start",
              mx: 0,
              "& .MuiCheckbox-root": { pt: 0.25, ml: -0.5 },
            }}
            control={
              <Checkbox
                checked={ackChecked}
                onChange={(e) => setAckChecked(e.target.checked)}
                autoFocus
              />
            }
            label={
              <Typography variant="body2" sx={{ lineHeight: 1.5, color: "text.primary" }}>
                {requiredText}
              </Typography>
            }
          />
        )}
        {mode === "match" && (
          <>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              To confirm, type the line below exactly.
            </Alert>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                backgroundColor: "grey.100",
                p: 1,
                borderRadius: 1,
                mb: 1,
                wordBreak: "break-word",
              }}
            >
              {requiredText}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type the line above"
              autoFocus
            />
          </>
        )}
        {mode === "reason" && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label={reasonLabel}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            helperText={`${reason.length}/500`}
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!canConfirm}
          variant="contained"
          color={confirmColor}
        >
          {isSubmitting ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
